import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Play,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Bot,
  Zap,
  MousePointer,
  Settings,
  RefreshCw,
  Plus,
  CalendarDays,
  ChevronRight,
  Sparkles,
  FileText,
  Database,
  Shield,
  Brain,
  BarChart3,
  Trash2,
} from "lucide-react";
import {
  apiv2,
  type ProjectOut,
  type ProjectActivityOut,
  type AgentRunOut,
  type AgentSessionSummary,
  type OrchestratorPlanOut,
} from "../apiv2";
import GreenTendrils from "./GreenTendrils";
import EpochSpendBadge from "./EpochSpendBadge";
import { RunOptionsBadges } from "./discovery/RunOptionsBadge";
import type { RunOptions } from "../api/profiler-api";

// ---------------------------------------------------------------------------
// Agent label lookup
// ---------------------------------------------------------------------------
const AGENT_LABELS: Record<
  string,
  { name: string; short: string; icon: React.ReactNode }
> = {
  "semantic-intelligence": {
    name: "Agent Semantic Intelligence",
    short: "Semantic Intelligence",
    icon: <Database size={16} />,
  },
  "backlog-architect": {
    name: "Agent Scrum Planner",
    short: "Scrum Planner",
    icon: <FileText size={16} />,
  },
  "data-guardian": {
    name: "Agent Data Guardian",
    short: "Data Guardian",
    icon: <Shield size={16} />,
  },
  "metadata-curator": {
    name: "Agent Metadata Curator",
    short: "Meta Curator",
    icon: <Brain size={16} />,
  },
  // April 2026: DQ Rules Engine merged into the Profiler agent. Old
  // rule-engine runs in the BMAD activity feed surface under this same
  // label so the merger is visually transparent for historical work.
  "data-profiler": {
    name: "Data Profiler & Quality Rules Engine",
    short: "Data Profiler",
    icon: <BarChart3 size={16} />,
  },
  "rules-engine": {
    name: "Data Profiler & Quality Rules Engine",
    short: "Data Profiler",
    icon: <BarChart3 size={16} />,
  },
};

const SESSION_TYPE_LABELS: Record<string, string> = {
  semantic: "Semantic Modeling",
  masking: "Data Masking",
  metadata: "Metadata Catalog",
  backlog: "Backlog Architect",
  profiling: "Data Profiling",
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------
type TabKey = "in-flight" | "completed";

function isInFlight(status: string): boolean {
  return [
    "pending",
    "queued",
    "running",
    "active",
    "waiting_input",
    "waiting_approval",
  ].includes(status);
}

function isCompleted(status: string): boolean {
  return [
    "completed",
    "failed",
    "approved",
    "rejected",
    "expired",
    "cancelled",
    "skipped",
  ].includes(status);
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<
    string,
    { label: string; classes: string; icon: React.ReactNode }
  > = {
    pending: {
      label: "Pending",
      classes: "text-amber-400 bg-amber-500/10",
      icon: <Clock size={10} />,
    },
    queued: {
      label: "Queued",
      classes: "text-amber-400 bg-amber-500/10",
      icon: <Clock size={10} />,
    },
    running: {
      label: "Running",
      classes: "text-sky-400 bg-sky-500/10",
      icon: <Play size={10} />,
    },
    active: {
      label: "Active",
      classes: "text-sky-400 bg-sky-500/10",
      icon: <Play size={10} />,
    },
    waiting_input: {
      label: "Awaiting Input",
      classes: "text-amber-400 bg-amber-500/10",
      icon: <Clock size={10} />,
    },
    waiting_approval: {
      label: "Awaiting Approval",
      classes: "text-purple-400 bg-purple-500/10",
      icon: <Clock size={10} />,
    },
    completed: {
      label: "Completed",
      classes: "text-green-400 bg-green-500/10",
      icon: <CheckCircle2 size={10} />,
    },
    approved: {
      label: "Approved",
      classes: "text-brand-400 bg-brand-500/10",
      icon: <CheckCircle2 size={10} />,
    },
    failed: {
      label: "Failed",
      classes: "text-red-400 bg-red-500/10",
      icon: <XCircle size={10} />,
    },
    rejected: {
      label: "Rejected",
      classes: "text-red-400 bg-red-500/10",
      icon: <XCircle size={10} />,
    },
    expired: {
      label: "Expired",
      classes: "text-gray-400 bg-gray-500/10",
      icon: <AlertTriangle size={10} />,
    },
    cancelled: {
      label: "Cancelled",
      classes: "text-gray-400 bg-gray-500/10",
      icon: <XCircle size={10} />,
    },
  };
  const m = map[status] || {
    label: status,
    classes: "text-white/40 bg-white/5",
    icon: null,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.classes}`}
    >
      {m.icon} {m.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface Props {
  projectId: string;
  onBack: () => void;
  onStartNewWork: () => void;
  onDeleteProject: () => void;
  onResumeRun: (agentId: string) => void;
  onResumeSession: (sessionType: string, sessionId: string) => void;
  onSetup: () => void;
  onResumeOrchestrator: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ProjectWorkView({
  projectId,
  onBack,
  onStartNewWork,
  onDeleteProject,
  onResumeRun,
  onResumeSession,
  onSetup,
  onResumeOrchestrator,
}: Props) {
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectOut | null>(null);
  const [activity, setActivity] = useState<ProjectActivityOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("in-flight");
  // April 2026: Profile-run history is what end users actually care about
  // on the project home (the BMAD widgets below are mostly empty for
  // direct-mode projects). Fetched separately because the activity
  // endpoint is BMAD-only and doesn't surface ProfilingRun rows.
  type ProfileRunSummary = {
    run_id: string;
    started_at: string;
    completed_at: string;
    status: string;
    aggregate_score: number;
    tables_profiled: number;
    source_name: string;
    source_type: string;
    run_options?: RunOptions;
  };
  const [profileRuns, setProfileRuns] = useState<ProfileRunSummary[]>([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [proj, act] = await Promise.all([
        apiv2.getProject(projectId),
        apiv2.getProjectActivity(projectId),
      ]);
      setProject(proj);
      setActivity(act);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load project activity",
      );
    } finally {
      setLoading(false);
    }
    // Profile runs come from a separate endpoint outside the BMAD
    // activity feed. Best-effort fetch — failures don't block the rest
    // of the page from rendering.
    try {
      const res = await fetch(
        `/api/profiling-agent/runs?project_id=${encodeURIComponent(projectId)}&limit=5`,
        { credentials: "include" },
      );
      if (res.ok) {
        const data = await res.json();
        setProfileRuns((data.runs ?? []) as ProfileRunSummary[]);
      }
    } catch {
      // ignore — non-fatal for project home
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  // ── Categorize work items ──────────────────────────

  const inFlightRuns = (activity?.runs ?? []).filter((r) =>
    isInFlight(r.status),
  );
  const completedRuns = (activity?.runs ?? []).filter((r) =>
    isCompleted(r.status),
  );

  const inFlightSessions = (activity?.sessions ?? []).filter((s) =>
    isInFlight(s.status),
  );
  const completedSessions = (activity?.sessions ?? []).filter((s) =>
    isCompleted(s.status),
  );

  const inFlightPlans = (activity?.orchestrator_plans ?? []).filter(
    (p) => p.status === "active",
  );
  const completedPlans = (activity?.orchestrator_plans ?? []).filter(
    (p) => p.status !== "active",
  );

  const inFlightCount =
    inFlightRuns.length + inFlightSessions.length + inFlightPlans.length;
  const completedCount =
    completedRuns.length + completedSessions.length + completedPlans.length;

  // ── Render helpers ──────────────────────────────────

  const handleDeleteRun = async (e: React.MouseEvent, runId: string) => {
    e.stopPropagation();
    if (!confirm("Delete this agent run? This action cannot be undone."))
      return;
    try {
      await apiv2.deleteRun(projectId, runId);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete run");
    }
  };

  const handleDeleteSession = async (
    e: React.MouseEvent,
    sessionId: string,
  ) => {
    e.stopPropagation();
    if (!confirm("Delete this session? This action cannot be undone.")) return;
    try {
      await apiv2.deleteSession(projectId, sessionId);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete session");
    }
  };

  const handleDeletePlan = async (e: React.MouseEvent, planId: string) => {
    e.stopPropagation();
    if (
      !confirm("Delete this orchestrator plan? This action cannot be undone.")
    )
      return;
    try {
      await apiv2.deletePlan(projectId, planId);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete plan");
    }
  };

  const renderRunCard = (run: AgentRunOut, mode: "in-flight" | "completed") => {
    const agentInfo = AGENT_LABELS[run.agent_id];
    return (
      <div
        key={`run-${run.id}`}
        className="glass rounded-xl p-4 transition-all duration-200 cursor-pointer hover:border-brand-500/30 group"
        style={{
          background: "rgba(28,28,42,0.5)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
        onClick={() => onResumeRun(run.agent_id)}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-brand-400">
              {agentInfo?.icon || <Bot size={16} />}
            </span>
            <span className="text-sm font-semibold text-white">
              {agentInfo?.short || run.agent_id}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleDeleteRun(e, run.id)}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
              title="Delete run"
            >
              <Trash2 size={13} />
            </button>
            <StatusBadge status={run.status} />
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-white/30">
          <span>Started by {run.started_by_name || "Unknown"}</span>
          <span className="flex items-center gap-1">
            <CalendarDays size={9} /> {formatDate(run.created_at)}
          </span>
          <span className="ml-auto text-brand-400 flex items-center gap-1">
            {mode === "in-flight" ? "Continue" : "Review"}{" "}
            <ChevronRight size={10} />
          </span>
        </div>
      </div>
    );
  };

  const renderSessionCard = (
    session: AgentSessionSummary,
    mode: "in-flight" | "completed",
  ) => {
    const label =
      SESSION_TYPE_LABELS[session.session_type] || session.session_type;
    return (
      <div
        key={`session-${session.id}`}
        className="glass rounded-xl p-4 transition-all duration-200 cursor-pointer hover:border-brand-500/30 group"
        style={{
          background: "rgba(28,28,42,0.5)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
        onClick={() =>
          onResumeSession(session.session_type, session.resume_key)
        }
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-brand-400">
              <Bot size={16} />
            </span>
            <span className="text-sm font-semibold text-white">{label}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleDeleteSession(e, session.id)}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
              title="Delete session"
            >
              <Trash2 size={13} />
            </button>
            <StatusBadge status={session.status} />
          </div>
        </div>
        {session.label && (
          <p className="text-xs text-white/30 mb-2 truncate">{session.label}</p>
        )}
        <div className="flex items-center gap-3 text-[11px] text-white/30">
          {session.file_count > 0 && (
            <span>
              {session.file_count} file{session.file_count !== 1 ? "s" : ""}
            </span>
          )}
          <span className="flex items-center gap-1">
            <CalendarDays size={9} /> {formatDate(session.updated_at)}
          </span>
          <span className="ml-auto text-brand-400 flex items-center gap-1">
            {mode === "in-flight" ? "Resume" : "Review"}{" "}
            <ChevronRight size={10} />
          </span>
        </div>
      </div>
    );
  };

  const renderPlanCard = (
    plan: OrchestratorPlanOut,
    mode: "in-flight" | "completed",
  ) => {
    const completedSteps = plan.steps.filter(
      (s) => s.status === "completed",
    ).length;
    const totalSteps = plan.steps.length;
    return (
      <div
        key={`plan-${plan.id}`}
        className="glass rounded-xl p-4 transition-all duration-200 cursor-pointer hover:border-brand-500/30 group"
        style={{
          background: "rgba(28,28,42,0.5)",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
        onClick={() => onResumeOrchestrator()}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-purple-400">
              <Zap size={16} />
            </span>
            <span className="text-sm font-semibold text-white">
              Autonomous Plan
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleDeletePlan(e, plan.id)}
              className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
              title="Delete plan"
            >
              <Trash2 size={13} />
            </button>
            <StatusBadge status={plan.status} />
          </div>
        </div>
        <p className="text-xs text-white/30 mb-2 line-clamp-2">{plan.goal}</p>
        <div className="flex items-center gap-3 text-[11px] text-white/30">
          <span>
            {completedSteps}/{totalSteps} steps done
          </span>
          <span className="flex items-center gap-1">
            <CalendarDays size={9} /> {formatDate(plan.created_at)}
          </span>
          <span className="ml-auto text-purple-400 flex items-center gap-1">
            {mode === "in-flight" ? "Continue" : "Review"}{" "}
            <ChevronRight size={10} />
          </span>
        </div>
      </div>
    );
  };

  // ── Loading ─────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] relative">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <Loader2
          size={24}
          className="animate-spin text-brand-500 relative z-10"
        />
      </div>
    );
  }

  return (
    <div className="overflow-y-auto custom-scrollbar h-full relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* ── Header ────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in">
          <div className="flex items-center gap-5">
            <button
              onClick={onBack}
              className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all duration-300 hover:bg-brand-500/5"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <ArrowLeft size={18} className="text-white/50" />
            </button>
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-3"
                style={{ background: "rgba(134,188,37,0.08)" }}
              >
                <Sparkles size={12} />
                Project Activity
              </div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight">
                <span className="gradient-text-green">
                  {project?.name || "Project"}
                </span>
              </h1>
              {project?.description && (
                <p className="text-sm text-white/40 mt-1 max-w-lg">
                  {project.description}
                </p>
              )}
              <div className="flex items-center gap-3 mt-2">
                {project?.execution_mode && (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      project.execution_mode === "autonomous"
                        ? "text-purple-400/80 bg-purple-500/10"
                        : "text-sky-400/80 bg-sky-500/10"
                    }`}
                  >
                    {project.execution_mode === "autonomous" ? (
                      <Zap size={9} />
                    ) : (
                      <MousePointer size={9} />
                    )}
                    {project.execution_mode === "autonomous"
                      ? "Autonomous"
                      : "Direct"}
                  </span>
                )}
                <EpochSpendBadge
                  projectId={projectId}
                  onClick={() => navigate("/admin/epoch-spend")}
                />
                {project?.domain && (
                  <span className="text-[11px] text-brand-400/80 bg-brand-500/10 px-2 py-0.5 rounded-full">
                    {project.domain}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="p-2 rounded-lg text-white/30 hover:text-brand-500 hover:bg-brand-500/10 transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={onSetup}
              className="p-2 rounded-lg text-white/30 hover:text-brand-500 hover:bg-brand-500/10 transition"
              title="Project Settings"
            >
              <Settings size={16} />
            </button>
            <button
              onClick={() => {
                if (
                  confirm(
                    "Delete this project and all its work? This action cannot be undone.",
                  )
                ) {
                  onDeleteProject();
                }
              }}
              className="p-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition"
              title="Delete Project"
            >
              <Trash2 size={16} />
            </button>
            <button
              onClick={() => onStartNewWork()}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2"
              style={{
                background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                boxShadow:
                  "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
              }}
            >
              <Plus size={14} /> Start New Work
            </button>
          </div>
        </div>

        {/* ── Error ─────────────────────────────────────── */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs ml-4"
            >
              dismiss
            </button>
          </div>
        )}

        {/* ── Recent Profile Runs ────────────────────────────
            Surfaced at the top of the project home so users don't have
            to drill into the Data Profiler agent to see their last few
            runs. April 2026 discoverability fix — previously this lived
            only inside ViewToggleBar's History tab. */}
        {profileRuns.length > 0 && (
          <section
            className="rounded-xl border border-white/10 overflow-hidden"
            style={{ background: "rgba(28,28,42,0.45)" }}
          >
            <header className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <BarChart3 size={14} className="text-brand-400" />
                <h3 className="text-sm font-semibold text-white/85">
                  Recent Profile Runs
                </h3>
                <span className="text-[11px] text-white/40">
                  · last {profileRuns.length} of{" "}
                  {profileRuns.length === 5 ? "5+" : profileRuns.length}
                </span>
              </div>
              <button
                onClick={() =>
                  navigate(
                    `/projects/${projectId}/agents/data-profiler?tab=history`,
                  )
                }
                className="text-[11px] text-brand-400 hover:text-brand-300 inline-flex items-center gap-1"
                title="Open the Data Profiler History tab to browse, compare, and delete runs"
              >
                View all <ChevronRight size={12} />
              </button>
            </header>
            <ul className="divide-y divide-white/5">
              {profileRuns.map((r) => {
                const score = Math.round(r.aggregate_score ?? 0);
                const scoreColor =
                  score >= 90
                    ? "text-emerald-300"
                    : score >= 75
                      ? "text-amber-300"
                      : "text-red-300";
                const completedDate = r.completed_at
                  ? formatDate(r.completed_at)
                  : "—";
                return (
                  <li
                    key={r.run_id}
                    className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/agents/data-profiler?tab=history`,
                      )
                    }
                  >
                    <div className="col-span-3 text-[12px] text-white/70">
                      {completedDate}
                    </div>
                    <div
                      className="col-span-5 text-[12px] text-white/80 truncate"
                      title={r.source_name || "—"}
                    >
                      <span className="text-white/40 mr-1">
                        {r.source_type === "database" ? (
                          <Database size={10} className="inline" />
                        ) : r.source_type === "file" ? (
                          <FileText size={10} className="inline" />
                        ) : null}
                      </span>
                      {r.source_name || (
                        <span className="text-white/30 italic">
                          unnamed source
                        </span>
                      )}
                    </div>
                    <div className="col-span-2">
                      <div
                        className={`text-[13px] font-bold tabular-nums ${scoreColor}`}
                      >
                        {score}
                      </div>
                      <RunOptionsBadges
                        options={r.run_options}
                        className="mt-1"
                      />
                    </div>
                    <div className="col-span-1 text-[11px] text-white/45 tabular-nums">
                      {r.tables_profiled} tbl
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <span
                        className={`text-[11px] uppercase px-1.5 py-0.5 rounded ${
                          r.status === "completed"
                            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                            : "bg-white/5 text-white/55 border border-white/10"
                        }`}
                      >
                        {r.status}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* ── Recent Sessions — per-agent cards (masking / semantic / metadata / backlog) ── */}
        {(["masking", "semantic", "metadata", "backlog"] as const).map(
          (type) => {
            const sessions = (activity?.sessions ?? [])
              .filter((s) => s.session_type === type)
              .sort(
                (a, b) =>
                  new Date(b.updated_at).getTime() -
                  new Date(a.updated_at).getTime(),
              )
              .slice(0, 3);
            if (sessions.length === 0) return null;
            const agentSlug = {
              masking: "data-guardian",
              semantic: "semantic-intelligence",
              metadata: "metadata-curator",
              backlog: "backlog-architect",
            }[type];
            const label = SESSION_TYPE_LABELS[type] ?? type;
            const icon = {
              masking: <Shield size={14} className="text-brand-400" />,
              semantic: <Database size={14} className="text-brand-400" />,
              metadata: <Brain size={14} className="text-brand-400" />,
              backlog: <FileText size={14} className="text-brand-400" />,
            }[type];
            return (
              <section
                key={type}
                className="rounded-xl border border-white/10 overflow-hidden"
                style={{ background: "rgba(28,28,42,0.45)" }}
              >
                <header className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 bg-white/[0.02]">
                  <div className="flex items-center gap-2">
                    {icon}
                    <h3 className="text-sm font-semibold text-white/85">
                      Recent {label} Sessions
                    </h3>
                    <span className="text-[11px] text-white/40">
                      · last {sessions.length}
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/agents/${agentSlug}?tab=history`,
                      )
                    }
                    className="text-[11px] text-brand-400 hover:text-brand-300 inline-flex items-center gap-1"
                  >
                    View all <ChevronRight size={12} />
                  </button>
                </header>
                <ul className="divide-y divide-white/5">
                  {sessions.map((s) => (
                    <li
                      key={s.id}
                      className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-white/[0.03] transition-colors cursor-pointer"
                      onClick={() =>
                        navigate(
                          s.status === "active" || s.status === "completed"
                            ? `/projects/${projectId}/agents/${agentSlug}?session=${s.id}`
                            : `/projects/${projectId}/agents/${agentSlug}?tab=history`,
                        )
                      }
                    >
                      <div className="col-span-3 text-[12px] text-white/70">
                        {formatDate(s.updated_at)}
                      </div>
                      <div
                        className="col-span-7 text-[12px] text-white/80 truncate"
                        title={s.label || undefined}
                      >
                        {s.label || (
                          <span className="text-white/30 italic">
                            unnamed session
                          </span>
                        )}
                      </div>
                      <div className="col-span-2 flex justify-end">
                        <span
                          className={`text-[11px] uppercase px-1.5 py-0.5 rounded border ${
                            s.status === "completed"
                              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                              : s.status === "active"
                                ? "bg-sky-500/15 text-sky-300 border-sky-500/25"
                                : "bg-white/5 text-white/55 border-white/10"
                          }`}
                        >
                          {s.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          },
        )}

        {/* ── Tabs ──────────────────────────────────────── */}
        {/*
            Hide the BMAD-driven In-Flight / Completed tabs entirely when
            there's no orchestrator/agent work AND we have profiling
            history to surface above. The empty "No completed work yet"
            screen confused users with a Direct-mode profiling project
            because those tabs are wired to BMAD AgentRun / AgentSession
            / OrchestratorPlan records, not to the Data Profiler runs the
            user actually cares about. Keeping the tabs visible only when
            BMAD content exists or the user has no profile runs at all
            (so a brand-new project still gets the Start New Work CTA).
        */}
        {(inFlightCount > 0 ||
          completedCount > 0 ||
          profileRuns.length === 0) && (
          <>
            <div
              className="flex items-center gap-1 p-1 rounded-xl w-fit"
              style={{
                background: "rgba(28,28,42,0.5)",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <button
                onClick={() => setTab("in-flight")}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  tab === "in-flight"
                    ? "bg-brand-500/20 text-brand-400"
                    : "text-white/40 hover:text-white/60 hover:bg-white/5"
                }`}
              >
                <Play size={14} />
                In Flight
                {inFlightCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-[11px] bg-brand-500/20 text-brand-400 font-bold">
                    {inFlightCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setTab("completed")}
                className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
                  tab === "completed"
                    ? "bg-brand-500/20 text-brand-400"
                    : "text-white/40 hover:text-white/60 hover:bg-white/5"
                }`}
              >
                <CheckCircle2 size={14} />
                Completed
                {completedCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-[11px] bg-white/10 text-white/50 font-bold">
                    {completedCount}
                  </span>
                )}
              </button>
            </div>

            {/* ── Content ───────────────────────────────────── */}
            {tab === "in-flight" ? (
              <div className="space-y-6 animate-fade-in">
                {inFlightCount === 0 ? (
                  <div className="text-center py-16">
                    <Play size={48} className="mx-auto text-white/10 mb-4" />
                    <h3 className="text-lg font-semibold text-white/30 mb-2">
                      No work in flight
                    </h3>
                    <p className="text-sm text-white/20 mb-6">
                      Start new work to begin running agents on this project.
                    </p>
                    <button
                      onClick={() => onStartNewWork()}
                      className="px-5 py-2.5 rounded-xl text-sm font-bold text-white flex items-center gap-2 mx-auto"
                      style={{
                        background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                      }}
                    >
                      <Plus size={14} /> Start New Work
                    </button>
                  </div>
                ) : (
                  <>
                    {/* In-flight orchestrator plans */}
                    {inFlightPlans.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Zap size={12} className="text-purple-400" />{" "}
                          Autonomous Plans
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {inFlightPlans.map((p) =>
                            renderPlanCard(p, "in-flight"),
                          )}
                        </div>
                      </section>
                    )}

                    {/* In-flight runs */}
                    {inFlightRuns.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <ArrowRight size={12} className="text-sky-400" />{" "}
                          Agent Runs
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {inFlightRuns.map((r) =>
                            renderRunCard(r, "in-flight"),
                          )}
                        </div>
                      </section>
                    )}

                    {/* In-flight sessions */}
                    {inFlightSessions.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Bot size={12} className="text-brand-400" /> Active
                          Sessions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {inFlightSessions.map((s) =>
                            renderSessionCard(s, "in-flight"),
                          )}
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-6 animate-fade-in">
                {completedCount === 0 ? (
                  <div className="text-center py-16">
                    <CheckCircle2
                      size={48}
                      className="mx-auto text-white/10 mb-4"
                    />
                    <h3 className="text-lg font-semibold text-white/30 mb-2">
                      No completed work yet
                    </h3>
                    <p className="text-sm text-white/20">
                      Completed agent runs, sessions, and plans will appear
                      here.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Completed orchestrator plans */}
                    {completedPlans.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Zap size={12} className="text-purple-400" />{" "}
                          Autonomous Plans
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {completedPlans.map((p) =>
                            renderPlanCard(p, "completed"),
                          )}
                        </div>
                      </section>
                    )}

                    {/* Completed runs */}
                    {completedRuns.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <ArrowRight size={12} className="text-green-400" />{" "}
                          Agent Runs
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {completedRuns.map((r) =>
                            renderRunCard(r, "completed"),
                          )}
                        </div>
                      </section>
                    )}

                    {/* Completed sessions */}
                    {completedSessions.length > 0 && (
                      <section>
                        <h3 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Bot size={12} className="text-green-400" /> Past
                          Sessions
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {completedSessions.map((s) =>
                            renderSessionCard(s, "completed"),
                          )}
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
